import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Info, Linkedin, CheckCircle2, XCircle, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { useNavigate } from 'react-router-dom'
import LinkPilotProLogo from '@/assets/linkpilot-pro-logo.png';
import { Input } from "@/components/ui/input";

interface LinkedInStatus {
  isConnected: boolean;
  hasRequiredScopes: boolean;
  lastChecked: Date | null;
}

const Settings = () => {
  const [linkedInStatus, setLinkedInStatus] = useState<LinkedInStatus>({
    isConnected: false,
    hasRequiredScopes: false,
    lastChecked: null
  });
  const [isChecking, setIsChecking] = useState(true);
  const showStripeSubscription = useFeatureFlagEnabled('show-stripe-subscription')
  const showLinkedInCompanyLookup = useFeatureFlagEnabled('show-linkedin-company-lookup-submit')
  const navigate = useNavigate()
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [companyUrl, setCompanyUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Handle LinkedIn OAuth callback
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    
    if (code) {

      // Process the OAuth callback
      console.log('Invoking exchange-linkedin-token function with code...'); // Debug log
      supabase.functions.invoke('exchange-linkedin-token', {
        body: { code }
      })
      .then(({ data, error }) => {
        if (error) throw error;
        
        // Clear the URL parameters
        window.history.replaceState({}, '', '/settings');
        
        // Refresh LinkedIn status
        checkLinkedInConnection();
        
        toast.success('LinkedIn connected successfully!');
      })
      .catch((error) => {
        console.error('Detailed error processing LinkedIn callback:', error);
        toast.error('Failed to connect LinkedIn account');
      });
    } else {
      console.log('No LinkedIn code found, checking connection...'); // Debug log
      checkLinkedInConnection();
    }
  }, []);

  useEffect(() => {
    checkLinkedInConnection();
  }, []);

  const checkLinkedInConnection = async () => {
    try {
      setIsChecking(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Check if we have LinkedIn data in the profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('linkedin_access_token, linkedin_token_expires_at')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      const hasValidToken = profile?.linkedin_access_token && 
        profile?.linkedin_token_expires_at && 
        new Date(profile.linkedin_token_expires_at) > new Date();

      // If we have a token, verify it using our Edge Function
      let hasRequiredScopes = false;
      if (hasValidToken) {
        try {
          const { data, error } = await supabase.functions.invoke('linkedin-auth', {
            body: { 
              action: 'verify',
              token: profile.linkedin_access_token 
            }
          });

          if (error) throw error;
          hasRequiredScopes = data?.valid ?? false;
        } catch (error) {
          console.error('Error verifying LinkedIn token:', error);
        }
      }

      setLinkedInStatus({
        isConnected: hasValidToken,
        hasRequiredScopes,
        lastChecked: new Date()
      });

    } catch (error) {
      console.error('Error checking LinkedIn connection:', error);
      toast.error("Failed to check LinkedIn connection status");
    } finally {
      setIsChecking(false);
    }
  };

  const handleConnect = async () => {
    try {
      console.log('Initiating LinkedIn connection...'); // Debug log
      const { data, error } = await supabase.functions.invoke('linkedin-auth', {
        body: { action: 'initiate' }
      });

      console.log('LinkedIn initiate response:', { data, error }); // Debug log

      if (error) throw error;
      
      if (data?.authorizationUrl) {
        console.log('Redirecting to:', data.authorizationUrl); // Debug log
        window.location.href = data.authorizationUrl;
      }
    } catch (error) {
      console.error('Detailed LinkedIn connection error:', error); // More detailed error
      toast.error("Failed to initiate LinkedIn connection. Please try again.");
    }
  };

  const handleDisconnect = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error('User not authenticated');

      // Clear LinkedIn tokens from profile
      const { error } = await supabase
        .from('profiles')
        .update({
          linkedin_access_token: null,
          linkedin_token_expires_at: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      setLinkedInStatus({
        isConnected: false,
        hasRequiredScopes: false,
        lastChecked: new Date()
      });

      toast.success("LinkedIn disconnected successfully");
    } catch (error) {
      console.error('Error disconnecting LinkedIn:', error);
      toast.error("Failed to disconnect LinkedIn");
    }
  };

  useEffect(() => {
    checkSubscriptionStatus();
  }, []);

  const checkSubscriptionStatus = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('stripe-manage-subscriptions');
      
      if (error) throw error;
      setIsSubscribed(!!data?.isSubscribed);
    } catch (error) {
      console.error('Error checking subscription status:', error);
      setIsSubscribed(false);
    }
  };

  const handleSubscribe = async () => {
    if (isSubscribed) {
      try {
        const { data, error } = await supabase.functions.invoke('stripe-customer-portal', {
          method: 'POST'
        });
        
        if (error) throw error;
        
        if (data?.url) {
          window.location.href = data.url;
        } else {
          throw new Error('No portal URL received');
        }
      } catch (error) {
        console.error('Error accessing billing portal:', error);
        toast.error(error.message || 'Failed to access billing portal');
      }
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('stripe-checkout', {
        method: 'POST'
      });
      
      if (error) throw error;
      
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error initiating checkout:', error);
      toast.error(error.message || 'Failed to start checkout process');
    }
  };

  useEffect(() => {
    // Handle Stripe checkout success
    const params = new URLSearchParams(window.location.search)
    if (params.get('checkout') === 'success') {
      toast.success('Subscription successful!')
      // Clear the URL parameters
      window.history.replaceState({}, '', '/settings')
    }
  }, [])

  const handleCompanySubmit = async () => {
    // Validate URL format
    const urlPattern = /^https:\/\/www\.linkedin\.com\/company\/[\w-]+\/?$/;
    if (!urlPattern.test(companyUrl)) {
      toast.error("Please enter a valid LinkedIn company URL");
      return;
    }

    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('process-linkedin-company', {
        body: { companyUrl }
      });

      if (error) throw error;

      toast.success("Company URL submitted successfully");
      setCompanyUrl(""); // Clear input after successful submission
    } catch (error) {
      console.error('Error processing company URL:', error);
      toast.error("Failed to process company URL");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-2xl">
      <h1 className="text-2xl font-bold mb-6">Settings</h1>
      
      <div className="space-y-8">
        {/* Account Section */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Account</h2>
          {showStripeSubscription && (
            <Card className="text-sm">
              <CardHeader className="pb-0 pt-3">
                <img 
                  src={LinkPilotProLogo} 
                  alt="LinkPilot Pro" 
                  className="h-10 object-contain w-32"
                />
              </CardHeader>
              <CardContent className="pt-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-sm">Subscription</h3>
                    <p className="text-xs text-muted-foreground">
                      Get access to advanced features and increased limits
                    </p>
                  </div>
                  <Button 
                    onClick={handleSubscribe}
                    variant={isSubscribed ? "outline" : "default"}
                    size="sm"
                  >
                    <CreditCard className="mr-2 h-4 w-4" />
                    {isSubscribed ? "Manage Subscription" : "Subscribe"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Integrations Section */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Integrations</h2>
          <Card className="text-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">LinkedIn Connection</CardTitle>
                  <CardDescription className="text-xs">
                    Connect your LinkedIn account to enable posting
                  </CardDescription>
                </div>
                {!isChecking && (
                  <div className="flex items-center gap-2 text-xs">
                    {linkedInStatus.isConnected ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    <span>
                      {linkedInStatus.isConnected ? 'Connected' : 'Not Connected'}
                    </span>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isChecking ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  <span>Checking connection status...</span>
                </div>
              ) : linkedInStatus.isConnected ? (
                <div className="space-y-4">
                  <div className="text-sm text-muted-foreground">
                    Last verified: {linkedInStatus.lastChecked?.toLocaleString()}
                  </div>
                  <div className="flex gap-4">
                    <Button onClick={checkLinkedInConnection} variant="outline">
                      Verify Connection
                    </Button>
                    <Button onClick={handleDisconnect} variant="destructive">
                      Disconnect
                    </Button>
                  </div>
                </div>
              ) : (
                <Button onClick={handleConnect} className="w-full sm:w-auto">
                  <Linkedin className="mr-2 h-4 w-4" />
                  Connect LinkedIn
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Admin Section */}
        {showLinkedInCompanyLookup && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Admin</h2>
            <Card className="text-sm">
              <CardHeader>
                <CardTitle className="text-base">LinkedIn Company Submission</CardTitle>
                <CardDescription className="text-xs">
                  Enter a LinkedIn company URL to process
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="https://www.linkedin.com/company/example"
                    value={companyUrl}
                    onChange={(e) => setCompanyUrl(e.target.value)}
                    className="flex-1 text-sm"
                  />
                  <Button 
                    onClick={handleCompanySubmit}
                    disabled={isSubmitting || !companyUrl}
                    size="sm"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;